import {
  createStylesParams,
  StyleParamType,
  wixColorParam,
} from '@wix/tpa-settings';
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';

export type ImageStylesParams = {
  coverImageCornerRadiusTopLeft: StyleParamType.Number;
  coverImageCornerRadiusTopRight: StyleParamType.Number;
  coverImageCornerRadiusBottomLeft: StyleParamType.Number;
  coverImageCornerRadiusBottomRight: StyleParamType.Number;
  coverImageCornerRadiusLinked: StyleParamType.Number;
  coverImageOpacity: StyleParamType.Number;
  coverImageBackgroundColor: StyleParamType.Color;

  sectionImageCornerRadiusTopLeft: StyleParamType.Number;
  sectionImageCornerRadiusTopRight: StyleParamType.Number;
  sectionImageCornerRadiusBottomLeft: StyleParamType.Number;
  sectionImageCornerRadiusBottomRight: StyleParamType.Number;
  sectionImageCornerRadiusLinked: StyleParamType.Number;
  sectionImageOpacity: StyleParamType.Number;
  sectionImageBackgroundColor: StyleParamType.Color;

  itemImageCornerRadiusTopLeft: StyleParamType.Number;
  itemImageCornerRadiusTopRight: StyleParamType.Number;
  itemImageCornerRadiusBottomLeft: StyleParamType.Number;
  itemImageCornerRadiusBottomRight: StyleParamType.Number;
  itemImageCornerRadiusLinked: StyleParamType.Number;
  itemImageOpacity: StyleParamType.Number;
  itemImageBackgroundColor: StyleParamType.Color;

  unsellableItemImageCornerRadiusTopLeft: StyleParamType.Number;
  unsellableItemImageCornerRadiusTopRight: StyleParamType.Number;
  unsellableItemImageCornerRadiusBottomLeft: StyleParamType.Number;
  unsellableItemImageCornerRadiusBottomRight: StyleParamType.Number;
  unsellableItemImageCornerRadiusLinked: StyleParamType.Number;
  unsellableItemImageOpacity: StyleParamType.Number;
  unsellableItemImageBackgroundColor: StyleParamType.Color;
};

export const imageStylesParams = createStylesParams<ImageStylesParams>({
  coverImageCornerRadiusTopLeft: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderTopLeftRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Top-left corner radius of the large cover/header image at the top of the menu page',
      },
    },
  },
  coverImageCornerRadiusTopRight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderTopRightRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Top-right corner radius of the large cover/header image at the top of the menu page',
      },
    },
  },
  coverImageCornerRadiusBottomLeft: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderBottomLeftRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Bottom-left corner radius of the large cover/header image at the top of the menu page',
      },
    },
  },
  coverImageCornerRadiusBottomRight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderBottomRightRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Bottom-right corner radius of the large cover/header image at the top of the menu page',
      },
    },
  },
  coverImageCornerRadiusLinked: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.numberEnum,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Whether the cover/header image corner radii are edited together: 1 = all corners locked together, 0 = each independent',
      },
    },
  },
  coverImageOpacity: {
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
    metadata: {
      type: StyleType.opacity,
      defaultValue: { type: 'static', value: 100 },
      ai: {
        description:
          'Opacity of the large cover/header image at the top of the menu page',
      },
    },
  },
  coverImageBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0.06),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-1', opacity: 0.06 },
      ai: {
        description:
          'Background/placeholder color behind the large cover/header image at the top of the menu page',
      },
    },
  },

  sectionImageCornerRadiusTopLeft: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderTopLeftRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Top-left corner radius of the image for each menu section/category',
      },
    },
  },
  sectionImageCornerRadiusTopRight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderTopRightRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Top-right corner radius of the image for each menu section/category',
      },
    },
  },
  sectionImageCornerRadiusBottomLeft: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderBottomLeftRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Bottom-left corner radius of the image for each menu section/category',
      },
    },
  },
  sectionImageCornerRadiusBottomRight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderBottomRightRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Bottom-right corner radius of the image for each menu section/category',
      },
    },
  },
  sectionImageCornerRadiusLinked: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.numberEnum,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Whether the menu section/category image corner radii are edited together: 1 = all corners locked together, 0 = each independent',
      },
    },
  },
  sectionImageOpacity: {
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
    metadata: {
      type: StyleType.opacity,
      defaultValue: { type: 'static', value: 100 },
      ai: {
        description: 'Opacity of the image for each menu section/category',
      },
    },
  },
  sectionImageBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.06),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 0.06 },
      ai: {
        description:
          'Background/placeholder color behind the image for each menu section/category',
      },
    },
  },

  itemImageCornerRadiusTopLeft: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderTopLeftRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Top-left corner radius of the thumbnail image on each menu item',
      },
    },
  },
  itemImageCornerRadiusTopRight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderTopRightRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Top-right corner radius of the thumbnail image on each menu item',
      },
    },
  },
  itemImageCornerRadiusBottomLeft: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderBottomLeftRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Bottom-left corner radius of the thumbnail image on each menu item',
      },
    },
  },
  itemImageCornerRadiusBottomRight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderBottomRightRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Bottom-right corner radius of the thumbnail image on each menu item',
      },
    },
  },
  itemImageCornerRadiusLinked: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.numberEnum,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Whether the menu item thumbnail image corner radii are edited together: 1 = all corners locked together, 0 = each independent',
      },
    },
  },
  itemImageOpacity: {
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
    metadata: {
      type: StyleType.opacity,
      defaultValue: { type: 'static', value: 100 },
      ai: {
        description: 'Opacity of the thumbnail image on each menu item',
      },
    },
  },
  itemImageBackgroundColor: {
    type: StyleParamType.Color,
    // Transparent by default so a Fit-mode letterbox shows nothing until the
    // user picks a "Color behind image" in the Design tab.
    getDefaultValue: wixColorParam('color-5', 0),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 0.06 },
      ai: {
        description:
          'Background/placeholder color behind the thumbnail image on each menu item',
      },
    },
  },

  unsellableItemImageCornerRadiusTopLeft: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderTopLeftRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Top-left corner radius of the thumbnail image on each unavailable (unsellable) menu item',
      },
    },
  },
  unsellableItemImageCornerRadiusTopRight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderTopRightRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Top-right corner radius of the thumbnail image on each unavailable (unsellable) menu item',
      },
    },
  },
  unsellableItemImageCornerRadiusBottomLeft: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderBottomLeftRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Bottom-left corner radius of the thumbnail image on each unavailable (unsellable) menu item',
      },
    },
  },
  unsellableItemImageCornerRadiusBottomRight: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderBottomRightRadius,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Bottom-right corner radius of the thumbnail image on each unavailable (unsellable) menu item',
      },
    },
  },
  unsellableItemImageCornerRadiusLinked: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.numberEnum,
      defaultValue: { type: 'static', value: 0 },
      ai: {
        description:
          'Whether the unavailable (unsellable) menu item thumbnail image corner radii are edited together: 1 = all corners locked together, 0 = each independent',
      },
    },
  },
  unsellableItemImageOpacity: {
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
    metadata: {
      type: StyleType.opacity,
      defaultValue: { type: 'static', value: 100 },
      ai: {
        description:
          'Opacity of the thumbnail image on each unavailable (unsellable) menu item',
      },
    },
  },
  unsellableItemImageBackgroundColor: {
    type: StyleParamType.Color,
      
     // Transparent by default (same as itemImageBackgroundColor).
    getDefaultValue: wixColorParam('color-5', 0),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: { type: 'static', preset: 'color-5', opacity: 0.06 },
      ai: {
        description:
          'Background/placeholder color behind the thumbnail image on each unavailable (unsellable) menu item',
      },
    },
  },
});
